import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { LatLngTuple } from 'leaflet';
interface ReactLeafLetMapComponentProps {
  mapId?: string;
  center?: [number, number] | LatLngTuple;
  zoom?: number;
  height?: string | number;
  children?: React.ReactNode;
  fitBounds?: L.LatLngBoundsExpression | null;
  beforeToggle?: React.ReactNode;
  minZoom?: number;
  borderRadius?: number;
  // Hard-locks panning/zooming to a region (e.g. the Trip Plan dashboard
  // restricting to the Middle East) — Leaflet won't let the viewport
  // cross these bounds at all, as opposed to `fitBounds` which is just a
  // one-off "zoom to this" that the user can still pan away from
  // afterward. Undefined by default so every other existing caller keeps
  // panning/zooming freely worldwide.
  maxBounds?: L.LatLngBoundsExpression;
  // How hard the view "bounces back" when dragged past maxBounds — 1.0
  // means the edge is solid (no rubber-banding past it at all), matching
  // the "don't allow panning to another region" requirement. Only
  // meaningful once maxBounds is set.
  maxBoundsViscosity?: number;
  // Some maps (e.g. the Trip Plan dashboard) only ever need the standard
  // road layer — satellite adds a control the user doesn't want there.
  // Defaults to shown so every other existing caller is unaffected.
  showSatelliteToggle?: boolean;
}

// Leaflet sizes its tile grid off the container's dimensions at init time
// and never re-checks them — when the container resizes for a reason
// Leaflet itself didn't cause (e.g. a sidebar panel collapsing/expanding
// next to the map), the tile grid stays stale and the newly-exposed area
// renders as blank grey until the user pans/zooms. Watching the container
// with a ResizeObserver and calling invalidateSize() keeps the tile grid
// in sync with the actual on-screen size.
const MapAutoResize = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [map]);

  return null;
};

const MapDestroyOnUnmount = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const mapInstance = map;

    return () => {
      try {
        mapInstance.off(); // remove all event listeners
        mapInstance.remove(); // destroy the Leaflet instance

        if (container) {
          (container as any)._leaflet_id = undefined;
          delete (container as any)._leaflet_id;
        }
      } catch (e) {
        // swallow — map may already be removed
      }
    };
  }, []);

  return null;
};

const ReactLeafLetMapComponent: React.FC<ReactLeafLetMapComponentProps> = ({
  mapId = 'default-map',
  center = [24.7136, 46.6753],
  zoom = 12,
  height = '75vh',
  children,
  fitBounds = null,
  beforeToggle,
  minZoom = 5,
  borderRadius = 16,
  showSatelliteToggle = true,
  maxBounds,
  maxBoundsViscosity,
  ...props
}) => {
  const [mapType, setMapType] = useState<'standard' | 'satellite'>('standard');
  const [mapKey, setMapKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMapKey((k) => k + 1);
  }, [mapId]);

  const toggleMapType = () => setMapType((prev) => (prev === 'standard' ? 'satellite' : 'standard'));

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        style={{
          position: 'absolute',
          top: 10, // align with zoom buttons
          left: 50, // offset from zoom buttons (default zoom buttons width ~40px)
          zIndex: 1000, // above map controls
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {beforeToggle && <div style={{ maxWidth: 1000, minWidth: 300 }}>{beforeToggle}</div>}

        {showSatelliteToggle && (
          <div
            style={{
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(6px)',
              padding: '8px 14px',
              borderRadius: 0,
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              transition: '0.25s ease',
            }}
            onClick={toggleMapType}
          >
            {mapType === 'standard' ? '🛰 Satellite View' : '🗺 Standard View'}
          </div>
        )}
      </div>

      <MapContainer
        key={`${mapId}-${mapKey}`}
        center={center}
        zoom={zoom}
        style={{
          height,
          width: '100%',
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        }}
        preferCanvas={true}
        doubleClickZoom={false}
        minZoom={minZoom}
        maxBounds={maxBounds}
        maxBoundsViscosity={maxBoundsViscosity}
        {...props}
      >
        <MapDestroyOnUnmount />
        <MapAutoResize />
        {mapType === 'standard' ? (
          <TileLayer
            url='https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}'
            attribution='&copy; Google Maps'
            maxZoom={30}
            minZoom={3}
          />
        ) : (
          <TileLayer
            url='https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'
            attribution='&copy; Google Maps'
            maxZoom={30}
            minZoom={3}
          />
        )}

        {fitBounds && <FitBoundsToView bounds={fitBounds} />}

        {children}
      </MapContainer>
      <style>{`
        .leaflet-container {
          z-index: 0;
        }
        .leaflet-control-zoom {
          z-index: 1000; /* make sure toggle appears next to zoom buttons */
        }
        .leaflet-control-attribution {
          display: none;
        }
        .leaflet-popup-content {
          width: 500px !important;
          max-height: 800px;
          overflow: auto;
          word-wrap: break-word;
        }
        .leaflet-popup-content-wrapper {
          padding: 10px;
        }
      `}</style>
    </div>
  );
};

export default ReactLeafLetMapComponent;

/** FitBounds Component */
const FitBoundsToView: React.FC<{ bounds: L.LatLngBoundsExpression }> = ({ bounds }) => {
  const map = useMap();

  React.useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [bounds]);

  return null;
};
